import { formatDate, teamTimezone } from '@/lib/formatters';
import { addDays, dayInZone, wallClockTime as zonedWallClockTime, type CalendarDay } from '@shared/wall-clock';

/**
 * Snoozing a Conversation (T-48) or a task (T-50): it leaves the lists (or the Board) and comes
 * back at SNOOZE_HOUR on the day chosen, in the team's timezone (so everyone sees the same morning).
 */

export const SNOOZE_HOUR = 8;

/** The quick choices in the Snooze menu, in days from today. */
export const SNOOZE_PRESETS: Array<{ days: number; label: string }> = [
  { days: 1, label: 'Tomorrow' },
  { days: 2, label: 'In 2 days' },
  { days: 3, label: 'In 3 days' },
  { days: 7, label: 'In a week' },
  { days: 14, label: 'In 2 weeks' },
];

export type { CalendarDay };
export { addDays };

/** The team's timezone (Team settings), or UTC (decision 57 a). */
export const teamZone = teamTimezone;

/** Today's date where the team is. */
export function teamToday(now = new Date(), timeZone = teamZone()): CalendarDay {
  return dayInZone(now, timeZone);
}

/** The moment it's `hour`:00 on `date` in `timeZone` (checked twice, for a clock change that day). */
export function wallClockTime(date: CalendarDay, hour: number, timeZone = teamZone()): Date {
  return zonedWallClockTime(date, hour, timeZone);
}

/** When a snooze of `days` from today ends. */
export function snoozeUntil(days: number, now = new Date(), timeZone = teamZone()): Date {
  return wallClockTime(addDays(teamToday(now, timeZone), days), SNOOZE_HOUR, timeZone);
}

/** "Sat 27 Sept" (with the year when it isn't this one). */
export function snoozeDay(at: Date | string): string {
  const date = new Date(at);
  const thisYear = formatDate(date, { year: 'numeric' }) === formatDate(new Date(), { year: 'numeric' });
  return formatDate(date, thisYear ? { weekday: 'short', day: 'numeric', month: 'short' } : { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** Snoozed and not back yet. */
export function isSnoozed(snoozedUntil: string | null | undefined, now = Date.now()): boolean {
  return Boolean(snoozedUntil) && new Date(snoozedUntil!).getTime() > now;
}

/** Its snooze has ended and nothing has happened since (it's back in the lists, near the top). */
export function backFromSnooze(row: { snoozed_until: string | null; last_message_at: string | null }, now = Date.now()): boolean {
  if (!row.snoozed_until) return false;
  const until = new Date(row.snoozed_until).getTime();
  return until <= now && (!row.last_message_at || new Date(row.last_message_at).getTime() < until);
}
